// web/src/copilot/useFilterNotesPageAction.ts
//
// Slice 2 / Task F7.
//
// Registers the `filter_notes_page` CopilotKit action. When invoked by the
// agent, navigates the user to `/notes` with the provided filter params
// reflected in the URL — NotesPage already uses useSearchParams as the
// source of truth for filter state, so the chips/grouped-toggle update
// automatically on arrival.
//
// Spec §5.4 — the handler returns `{ navigated: true }` so the agent can
// confirm the side-effect happened.
import { useNavigate } from "react-router-dom";
import { useCopilotAction } from "@copilotkit/react-core";

type FilterArgs = {
  target_kind?: "cluster" | "analysis" | "material" | "freeform";
  material_id?: string;
  archived?: boolean;
  group_by?: "chronological" | "attachment";
};

export function useFilterNotesPageAction(): void {
  const navigate = useNavigate();
  useCopilotAction({
    name: "filter_notes_page",
    description:
      "Navigate to /notes with the given filters applied. Use this when " +
      "the user asks to filter, sort, or narrow their notes list (e.g. " +
      '"show only XAUUSD cluster notes").',
    parameters: [
      {
        name: "target_kind",
        type: "string",
        enum: ["cluster", "analysis", "material", "freeform"],
        required: false,
      },
      { name: "material_id", type: "string", required: false },
      { name: "archived", type: "boolean", required: false },
      {
        name: "group_by",
        type: "string",
        enum: ["chronological", "attachment"],
        required: false,
      },
    ],
    handler: async (args: FilterArgs) => {
      const params = new URLSearchParams();
      if (args.target_kind) params.set("target_kind", args.target_kind);
      if (args.material_id) params.set("material_id", args.material_id);
      if (args.archived) params.set("archived", "true");
      if (args.group_by) params.set("group_by", args.group_by);
      const qs = params.toString();
      navigate(qs ? `/notes?${qs}` : "/notes");
      return { navigated: true };
    },
  });
}
